import {Component, OnDestroy, OnInit, ViewContainerRef} from '@angular/core';
import {MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig} from '@angular/material/legacy-dialog';
import {MatLegacySnackBar as MatSnackBar, MatLegacySnackBarConfig as MatSnackBarConfig} from '@angular/material/legacy-snack-bar';
import {NavigationEnd, Router, RouterEvent} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {combineLatest, combineLatestWith, Subject, Subscription} from 'rxjs';
import {distinctUntilChanged, filter, startWith, takeUntil} from 'rxjs/operators';

import {DialogAnnouncementComponent} from '../dialog-announcement/dialog-announcement.component';
import {ngcAnimations} from '@app/core/comp/animations';
import {
    AnnouncementService, ChangeCliemEvent, Claim, DialogEvent, EventNames, InputEvent, PolicyConfig
} from '../announcement.service';
import {DialogExtInfoComponent} from '../dialog-extinfo/dialog-extinfo.component';
import {
    getAuthenticatedUser,
    isAuthenticated,
    isVisibleContent,
    State
} from '@app/app.reducers';
import {User} from '@app/core/model/user';
import {MESSAGES} from '@app/core/model/messages';
import * as authAction from '@app/views/common/auth.actions';
import {NotificationDialogComponent} from '@app/core/comp/dialog/notification-dialog/notification-dialog.component';

@Component({
    selector   : 'ngc-announcement-launcher',
    templateUrl: './announcement-launcher.component.html',
    styleUrls  : ['./announcement-launcher.component.scss'],
    animations : ngcAnimations
})
export class AnnouncementLauncherComponent implements OnInit, OnDestroy {
    private currentUser: User;
    private unsubscribe$ = new Subject<void>();
    private passwordExpirationNotificationSubscriptions: Subscription[];
    private setUpMFAForVendorsNotificationSubscriptions: Subscription[];

    constructor(
        private store: Store<State>,
        private router: Router,
        private announcementService: AnnouncementService,
        private snackBar: MatSnackBar,
        public viewContainerRef: ViewContainerRef,
        public dialog: MatDialog
    ) {
        this.announcementService.onDialogEvents.pipe(takeUntil(this.unsubscribe$)).subscribe({
            next: (event: DialogEvent) => {
                if (event) {
                    switch (event.type) {
                        case 'announcement':
                            const conf: PolicyConfig = this.announcementService.policyConf.get(event.name);
                            if (conf && conf.claims.length > 0) {
                                const ads = this.dialog.openDialogs.filter(ref => ref?.id === 'announcement-dialog');
                                if (ads.length === 0) {
                                    conf.reset();
                                    this.openAnnouncementDialog(conf);
                                }
                            }
                            break;
                        case 'extinfo':
                            this.openExtInfoDialog(event);
                            break;
                        case 'passwordExpireNotification':
                            this.onOpenPasswordExpirationNotificationDialog(event);
                            break;
                        case 'setUpMFAForVendorsDeadlineExpireNotification':
                            this.onOpenSetUpMFAForVendorsNotificationDialog(event);
                            break;
                    }
                }
            }
        });

        this.announcementService.onChangeClaimEvents.pipe(takeUntil(this.unsubscribe$))
            .subscribe((event: ChangeCliemEvent) => {
                if (event && ['accepted', 'rejected', 'error'].indexOf(event.type) > -1) {
                    const conf: PolicyConfig = this.announcementService.policyConf.get(event.name);
                    if (conf) {
                        if (['rejected', 'error'].indexOf(event.type) && event.claim.rule.rejectCommand === 'logout') {
                            return;
                        }
                        this.openAnnouncementDialog(conf);
                    }

                    if (event.type == 'error') {
                        this.snackBar.open(MESSAGES.UNKNOWN_ERROR, '', <MatSnackBarConfig>{duration: 3000});
                    }
                }
            }
        );

        this.store.pipe(
            select(getAuthenticatedUser),
            takeUntil(this.unsubscribe$)
        )
            .subscribe(user => this.currentUser = user);
    }

    ngOnInit(): void {
        this.store.pipe(
            select(isVisibleContent),
            distinctUntilChanged(),
            filter(value => !!value),
            takeUntil(this.unsubscribe$)
        )
            .subscribe(() => {
                    const announcementDialog = this.dialog.getDialogById('announcement-dialog');

                    if (announcementDialog) {
                        announcementDialog.close();
                    }
                }
            );

        combineLatest([
            this.store.pipe(select(getAuthenticatedUser)),
            this.store.pipe(select(isAuthenticated)),
            this.store.pipe(select(isVisibleContent), distinctUntilChanged()),
            this.router.events.pipe(filter(event => event instanceof NavigationEnd))
        ]).pipe(
            filter(([user, isAuthenticated, isVisibleContent, event]: [User, boolean, boolean, RouterEvent]) =>
                user && !!user.guid && isAuthenticated && !isVisibleContent && !this.announcementService.isCheckingClaimsForAuthentication.value
            ),
            takeUntil(this.unsubscribe$)
        ).subscribe(([user, isAuthenticated, isVisibleContent, event]: [User, boolean, boolean, RouterEvent]) => {
                this.store.dispatch(new authAction.CheckAcceptedPrivacyPolicyAction({user: user}));
            }
        );
    }

    openAnnouncementDialog(conf: PolicyConfig): void {

        const claim: Claim = conf.getNextClaim();
        if (!claim) {
            return;
        }

        const displayOnly = !this.currentUser ||
            !(this.currentUser.primaryAdmin && this.currentUser.role === 'INSTITUTIONAL_ADMIN') ||
            conf.eventName == EventNames.ClaimRequest;

        const announcementConfig = <MatDialogConfig>{
            viewContainerRef: this.viewContainerRef,
            autoFocus: false,
            disableClose: conf.eventName == EventNames.Authentication,
            hasBackdrop: conf.eventName !== EventNames.Authentication,
            id: 'announcement-dialog',
            width: '80%',
            minWidth: '300px',
            minHeight: '300px',
            maxWidth: '95%',
            maxHeight: '95%',
            data: {
                claim: claim,
                name: conf.eventName,
                currentIndex: conf.claimIndex,
                total: conf.getTotalClaims(),
                displayOnly: displayOnly
            }
        };
        const announcementDialogRef = this.dialog.open(DialogAnnouncementComponent, announcementConfig);
        announcementDialogRef.afterOpened().pipe(takeUntil(this.unsubscribe$)).subscribe(value => {
            this.announcementService.onInputEvents.emit(<InputEvent>{
                eventName: 'DialogOpened',
                claim: claim,
                name: conf.eventName
            });
        });
        announcementDialogRef.afterClosed().pipe(takeUntil(this.unsubscribe$)).subscribe(result => {
            if (result === 'accept') {
                this.announcementService.onInputEvents.emit(<InputEvent>{
                    eventName: 'ClaimAccepted',
                    claim: claim,
                    name: conf.eventName
                });
            } else if (result === 'reject') {
                this.announcementService.onInputEvents.emit(<InputEvent>{
                    eventName: 'ClaimRejected',
                    claim: claim,
                    name: conf.eventName
                });
            } else {
                this.announcementService.onInputEvents.emit(<InputEvent>{
                    eventName: 'DialogClosed',
                    claim: claim,
                    name: conf.eventName
                });
            }
        });
    }

    openExtInfoDialog(event: DialogEvent): void {

        const extinfoConfig = <MatDialogConfig>{
            viewContainerRef: this.viewContainerRef,
            autoFocus: false,
            hasBackdrop: false,
            id: 'extinfo-dialog',
            width: '90%',
            maxWidth: 'none',
            minWidth: '65vh',
            minHeight: '70px',
            data: {
                message: event.message,
                displayOnly: true
            }
        };
        const extinfoDialogRef = this.dialog.open(DialogExtInfoComponent, extinfoConfig);
        extinfoDialogRef.afterOpened().pipe(takeUntil(this.unsubscribe$)).subscribe(value => {
            this.announcementService.onInputEvents.emit(<InputEvent>{
                eventName: 'ExtInfoOpened',
                claim: null,
                name: event.name
            });
        });
        extinfoDialogRef.afterClosed().pipe(takeUntil(this.unsubscribe$)).subscribe(result => {

            this.announcementService.onInputEvents.emit({
                eventName: 'ExtInfoClosed',
                claim: null,
                name: event.name
            });

            /* if (claim && claim.rule.acceptanceRequired) {
                this.announcementService.onDialogEvents.next(<DialogEvent>{
                    type: 'announcement',
                    name: EventNames.Authentication,
                    claims: [claim],
                    message: null,
                    attribute: event.attribute
                });
            }*/
        });

    }

    onOpenPasswordExpirationNotificationDialog(event: DialogEvent): void {
        const notificationEvent = event;
        this.passwordExpirationNotificationSubscriptions = [];

        this.passwordExpirationNotificationSubscriptions.push(
            this.announcementService.allClaimAcceptedEvents$
                .pipe(
                    takeUntil(this.unsubscribe$),
                    filter((allClaimAccepted: boolean) => allClaimAccepted),
                    startWith(null),
                    combineLatestWith(this.announcementService.onChangeClaimEvents.pipe(
                        takeUntil(this.unsubscribe$),
                        startWith(null)
                        )
                    )
                )
                .subscribe(([allClaimAccepted, event]: [boolean, ChangeCliemEvent]) => {

                    const authenticationPolicyConfig: PolicyConfig = this.announcementService.policyConf.get(EventNames.Authentication);
                    if (((authenticationPolicyConfig?.claims
                                && (authenticationPolicyConfig.claims.length > 0)
                                && authenticationPolicyConfig.isAllAccepted())
                            || (authenticationPolicyConfig.claims.length === 0))
                        && allClaimAccepted) {

                        this.announcementService.allClaimAcceptedEvents.next(false);
                        this.openPasswordExpirationNotificationDialog(notificationEvent, true);
                    }

                    if (event && ['rejected', 'error'].indexOf(event.type) > -1) {
                        const conf: PolicyConfig = this.announcementService.policyConf.get(event.name);
                        if (conf?.claims?.length
                            && (event.claim?.id === conf.claims[conf.claims?.length - 1]?.id)) {
                            this.openPasswordExpirationNotificationDialog(notificationEvent, false);
                        }
                    }
                }));
    }

    openPasswordExpirationNotificationDialog(event: DialogEvent, allClaimAccepted: boolean, subscription?: Subscription): void {
        const notificationDialogConfig = <MatDialogConfig>{
            viewContainerRef: this.viewContainerRef,
            autoFocus: false,
            hasBackdrop: true,
            id: 'notification-dialog',
            maxWidth: '600px',
            minWidth: '30vw'
        };
        const dialogRef = this.dialog.open(NotificationDialogComponent, notificationDialogConfig);

        dialogRef.componentInstance.header = `Update your password`;
        dialogRef.componentInstance.target = event.message;
        dialogRef.componentInstance.notificationLink = 'user/profile/securitySettings';

        dialogRef.afterClosed().pipe(takeUntil(this.unsubscribe$)).subscribe(result => {
            this.passwordExpirationNotificationSubscriptions.forEach(subscription => subscription.unsubscribe());
            this.passwordExpirationNotificationSubscriptions = null;

            if (allClaimAccepted) {
                this.announcementService.allClaimAcceptedEvents.next(true);
            }

            if (!this.setUpMFAForVendorsNotificationSubscriptions) {
                this.announcementService.isClaimingAnnouncementsIsInProgress.next(false);
            }
        });
    }

    onOpenSetUpMFAForVendorsNotificationDialog(event: DialogEvent): void {
        const notificationEvent = event;
        this.setUpMFAForVendorsNotificationSubscriptions = [];

        this.setUpMFAForVendorsNotificationSubscriptions.push(
            this.announcementService.allClaimAcceptedEvents$
                .pipe(
                    takeUntil(this.unsubscribe$),
                    filter((allClaimAccepted: boolean) => allClaimAccepted && !this.passwordExpirationNotificationSubscriptions),
                    startWith(null),
                    combineLatestWith(this.announcementService.onChangeClaimEvents.pipe(
                        takeUntil(this.unsubscribe$),
                        startWith(null)
                        )
                    )
                )
                .subscribe(([allClaimAccepted, event]: [boolean, ChangeCliemEvent]) => {

                    const authenticationPolicyConfig: PolicyConfig = this.announcementService.policyConf.get(EventNames.Authentication);
                    if (((authenticationPolicyConfig?.claims
                                && (authenticationPolicyConfig.claims.length > 0)
                                && authenticationPolicyConfig.isAllAccepted())
                            || (authenticationPolicyConfig.claims.length === 0))
                        && allClaimAccepted) {

                        this.announcementService.allClaimAcceptedEvents.next(false);
                        this.openSetUpMFAForVendorsNotificationDialog(notificationEvent, true);
                    }

                    if (event && ['rejected', 'error'].indexOf(event.type) > -1) {
                        const conf: PolicyConfig = this.announcementService.policyConf.get(event.name);
                        if (conf?.claims?.length
                            && (event.claim?.id === conf.claims[conf.claims?.length - 1]?.id)) {
                            this.openSetUpMFAForVendorsNotificationDialog(notificationEvent, false);
                        }
                    }
                }));
    }

    openSetUpMFAForVendorsNotificationDialog(event: DialogEvent, allClaimAccepted: boolean, subscription?: Subscription): void {
        const notificationDialogConfig = <MatDialogConfig>{
            viewContainerRef: this.viewContainerRef,
            autoFocus: false,
            hasBackdrop: true,
            id: 'notification-dialog',
            maxWidth: '600px',
            minWidth: '30vw'
        };
        const dialogRef = this.dialog.open(NotificationDialogComponent, notificationDialogConfig);

        dialogRef.componentInstance.header = `Set Up Multi-Factor Authentication (MFA)`;
        dialogRef.componentInstance.target = event.message;
        dialogRef.componentInstance.notificationLink = 'user/profile/securitySettings';

        dialogRef.afterClosed().pipe(takeUntil(this.unsubscribe$)).subscribe(result => {
            this.setUpMFAForVendorsNotificationSubscriptions.forEach(subscription => subscription.unsubscribe());
            this.setUpMFAForVendorsNotificationSubscriptions = null;

            if (allClaimAccepted) {
                this.announcementService.allClaimAcceptedEvents.next(true);
                this.announcementService.isClaimingAnnouncementsIsInProgress.next(false);
            }
        });
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
        this.passwordExpirationNotificationSubscriptions.forEach(subscription => subscription.unsubscribe());
        this.setUpMFAForVendorsNotificationSubscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
